/**
 * Cross-tab notice that the Description Workbench wrote into the
 * Detailed Description.
 *
 * The Spec header's "Description ↗" link opens the workbench in a new
 * tab (a real <a target="_blank">), so the usual shape is two tabs.
 * When the drafter accepts a claim in the workbench tab, the Spec tab
 * has no way of knowing, and before this it refreshed only the section
 * on screen, and only on focus. The drafting team reported the accepted
 * text as "not found under the Description page". A storage event
 * reaches every OTHER same-origin tab the moment the key is written, so
 * the Spec tab refreshes at once.
 *
 * The same-tab case (a deep link to #/description/… followed by Back)
 * needs none of this: SpecPage remounts and its same-matter remount
 * refresh re-reads the rail and drops any paragraph cache whose count
 * no longer matches the server.
 */
export const DD_ACCEPTED_STORAGE_KEY = 'claimstudio.spec.dd.accepted';

export interface DdAcceptedNotice {
  matterId: string;
  claimNumber: number;
  at: number;
}

/** Write the notice. Swallows storage errors (private mode, quota,
 * blocked storage) — the focus refresh still covers that case. */
export function broadcastDdAccepted(matterId: string, claimNumber: number): void {
  try {
    const notice: DdAcceptedNotice = { matterId, claimNumber, at: Date.now() };
    window.localStorage.setItem(DD_ACCEPTED_STORAGE_KEY, JSON.stringify(notice));
  } catch {
    // Nothing to do; see above.
  }
}

/** Parse a storage event's value; null when it is not our notice. */
export function readDdAcceptedNotice(ev: StorageEvent): DdAcceptedNotice | null {
  if (ev.key !== DD_ACCEPTED_STORAGE_KEY || !ev.newValue) return null;
  try {
    const parsed = JSON.parse(ev.newValue) as Partial<DdAcceptedNotice>;
    if (typeof parsed.matterId !== 'string') return null;
    return {
      matterId: parsed.matterId,
      claimNumber: Number(parsed.claimNumber) || 0,
      at: Number(parsed.at) || 0,
    };
  } catch {
    return null;
  }
}
